// Supabase Edge Functions Manager Component
// Displays and manages user's Edge Functions

import { useState, useEffect, useCallback } from 'react';
import { Code2, RefreshCw, Play, ExternalLink, Zap } from 'lucide-react';
import { useSupabaseMCP } from '@/hooks/useSupabaseMCP';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PxMark } from '@/components/PxMark';
import { cn } from '@/lib/utils';

interface EdgeFunction {
    id: string;
    slug: string;
    name: string;
    version: number;
    status: 'ACTIVE' | 'INACTIVE' | 'THROTTLED';
    created_at: string;
    updated_at: string;
}

interface SupabaseEdgeFunctionsManagerProps {
    pixelProjectId?: string;
}

export function SupabaseEdgeFunctionsManager({ pixelProjectId }: SupabaseEdgeFunctionsManagerProps) {
    const { listEdgeFunctions, isConnected, selectedProject } = useSupabaseMCP(pixelProjectId);
    const [functions, setFunctions] = useState<EdgeFunction[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadFunctions = useCallback(async () => {
        if (!isConnected) return;

        setIsLoading(true);
        setError(null);

        try {
            const result = await listEdgeFunctions();
            if (result.success && result.data) {
                const functionsData = Array.isArray(result.data) ? result.data : [];
                setFunctions(functionsData);
            } else {
                setError(result.error || 'Failed to load Edge Functions');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [isConnected, listEdgeFunctions]);

    useEffect(() => {
        if (isConnected) {
            loadFunctions();
        }
    }, [isConnected, loadFunctions]);

    // Badge d'état mono 10 uppercase — sémantique ok/faint/warn
    const getStatusBadge = (status: string) => {
        const base = 'px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em]';
        switch (status) {
            case 'ACTIVE':
                return <span className={cn(base, 'bg-ok/10 text-ok')}>Active</span>;
            case 'INACTIVE':
                return <span className={cn(base, 'bg-secondary text-faint')}>Inactive</span>;
            case 'THROTTLED':
                return <span className={cn(base, 'bg-warn/10 text-warn')}>Limitée</span>;
            default:
                return <span className={cn(base, 'bg-secondary text-faint')}>{status}</span>;
        }
    };

    const formatDate = (date: string) => {
        try {
            return new Date(date).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return date;
        }
    };

    const getFunctionUrl = (slug: string) => {
        if (!selectedProject) return null;
        return `https://${selectedProject}.supabase.co/functions/v1/${slug}`;
    };

    if (!isConnected) {
        return (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center">
                <PxMark state="idle" size={24} className="mb-4" />
                <h3 className="mb-2 text-[16px] font-medium text-foreground">Supabase non connecté</h3>
                <p className="text-sm text-muted-foreground">
                    Connectez votre compte Supabase via le bouton dans la sidebar.
                </p>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border p-4">
                <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-muted-foreground" />
                    <h2 className="text-[13px] font-medium text-foreground">Edge Functions</h2>
                    <span className="font-mono text-[11px] text-faint">({functions.length})</span>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadFunctions}
                    disabled={isLoading}
                    className="gap-2"
                >
                    <RefreshCw className="h-4 w-4" />
                    Actualiser
                </Button>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1">
                {isLoading && functions.length === 0 ? (
                    <div className="flex items-center justify-center p-8">
                        <PxMark state="thinking" size={20} />
                    </div>
                ) : error ? (
                    <div className="border-l-2 border-err m-4 bg-err/5 p-3 font-mono text-[12px] text-foreground">{error}</div>
                ) : functions.length === 0 ? (
                    <div className="p-8 text-center">
                        <PxMark state="idle" size={24} className="mx-auto mb-3" />
                        <p className="mb-4 text-sm text-muted-foreground">Aucune Edge Function trouvée.</p>
                        <p className="text-xs text-muted-foreground">
                            Demandez à Pixel de créer une Edge Function pour vous.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2 p-2">
                        {functions.map((fn) => (
                            <div
                                key={fn.id}
                                className="border border-border bg-card p-4 transition-colors hover:bg-secondary"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                        <div className="mb-1 flex items-center gap-2">
                                            <Code2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                                            <h3 className="truncate font-mono text-[13px] font-medium text-foreground">{fn.slug}</h3>
                                            {getStatusBadge(fn.status)}
                                        </div>
                                        <div className="space-y-0.5 font-mono text-[11px] text-muted-foreground">
                                            <p>Version : {fn.version}</p>
                                            <p>Mis à jour : {formatDate(fn.updated_at)}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 shrink-0">
                                        {getFunctionUrl(fn.slug) && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 gap-1.5 text-xs"
                                                onClick={() => window.open(getFunctionUrl(fn.slug)!, '_blank')}
                                            >
                                                <ExternalLink className="w-3.5 h-3.5" />
                                                Ouvrir
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 gap-1.5 text-xs"
                                            onClick={() => {
                                                const url = getFunctionUrl(fn.slug);
                                                if (url) {
                                                    fetch(url).then(res => res.json()).then(console.log).catch(console.error);
                                                }
                                            }}
                                        >
                                            <Play className="w-3.5 h-3.5" />
                                            Test
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </ScrollArea>
        </div>
    );
}

export default SupabaseEdgeFunctionsManager;
